import React, { useEffect, useState } from 'react'
import { connect } from '@cerebral/react'
import { Box, Button, ButtonGroup, Card, Collapse, Grid2 as Grid, IconButton, Link, TableCell, TableRow, Tooltip, Typography } from '@mui/material'
import { InfoOutlined, KeyboardArrowDown, KeyboardArrowUp, PersonAddAlt1, Schedule } from '@mui/icons-material'
import { props, sequences, state } from 'cerebral'
import { DateTime } from 'luxon'
import * as format from '../../../../lib/util/format'
import { entityFromState } from '../../../../modules/computed'
import { routePaths } from '../../../../modules/route'
import Sortable from '../../../blocks/Sortable'
import FilterBy from '../../../blocks/FilterBy'
import TableList from '../../../blocks/TableList'
import { RoutePaths } from '../../../../types/Route'
import { Appointment, ConnectionBoardItem, Score } from '../../../../types/ConnectionBoard'
import ConnectionScore from './ConnectionScore'
import ConnectionSignals from './ConnectionSignals'
import { OneToOneActivity } from '../../../../assets/icons'
import { icons, defaultLabels } from '../../surveys/selections'
import levelsOfCare from '../../../../../shared/levels-of-care'
import { descend, isEmpty, pluck, propOr } from 'ramda'
import { Organization } from '../../../../types/Organization'

interface RowProps {
  id: string
  type: string
}

interface RowConnectedProps {
  item: ConnectionBoardItem
  appointmentsByProfile: Record<string, Appointment>
  appointmentsEnabled: boolean
}

const filterOptions = levelsOfCare.filter(Boolean).map((levelOfCare) => ({ value: levelOfCare, label: levelOfCare }))

interface MoodProps {
  score?: Score & {
    mostRecent?: {
      value?: number | null
      changedAt?: Date
    }
  }
}

const Mood = (props: MoodProps) => {
  const { score: { value, mostRecent = {} } = {} } = props
  const moodScore = value === null ? mostRecent.value : value
  const MoodIcon = typeof moodScore === 'number' ? icons.faces?.[moodScore] : null

  if (!MoodIcon) {
    return <>-</>
  }

  const hasRecentResponse = value === null && !isEmpty(mostRecent)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
      <Tooltip title={defaultLabels.faces[value as number]}>
        <MoodIcon fontSize="medium" color={hasRecentResponse ? 'action' : 'primary'} />
      </Tooltip>

      {hasRecentResponse ? (
        <Typography variant="body2" ml={1}>
          No response today
        </Typography>
      ) : null}
    </Box>
  )
}

interface BoardMessageConnectedProps {
  flagInactiveAfterDays?: number
  filter: string
}

const BoardMessage = connect<object, BoardMessageConnectedProps>(
  {
    flagInactiveAfterDays: state`account.organization.flagInactiveAfterDays`,
    filter: state`route.query.filter`,
  },

  ({ flagInactiveAfterDays = 7, filter }: BoardMessageConnectedProps) => {
    const isFilteringForInactive = filter === 'connectionBoardInactive'

    return (
      <Box
        display="flex"
        alignItems="flex-start"
        gap={1}
        p={1.5}
        sx={{ bgcolor: 'grey.50', borderLeft: 4, borderColor: isFilteringForInactive ? 'primary.main' : 'transparent' }}
        borderRadius={2}
      >
        <InfoOutlined sx={{ color: 'text.secondary', alignSelf: 'center' }} />

        {isFilteringForInactive ? (
          <Typography variant="body1" color="text.secondary" fontWeight={500}>
            Showing only residents with no activity for {flagInactiveAfterDays}+ days.{' '}
            <Link sx={{ marginLeft: 2 }} variant="body1" href={(routePaths as RoutePaths).toRoot({ filter: 'connectionBoard' })}>
              Show all
            </Link>
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Signals are based on research-validated indicators of wellbeing risk in older adults — including activity participation, self-reported mood, and
            significant life dates.
          </Typography>
        )}
      </Box>
    )
  }
)

const intro = (
  <Grid container spacing={2} alignItems="center">
    <Grid size={{ xs: 9 }}>
      <BoardMessage />
    </Grid>

    <Grid size={{ xs: 3 }}>
      {/* @ts-expect-error FilterBy does not have types defined */}
      <FilterBy options={filterOptions} param="levelOfCare" placeholder="Level of Care" optionPrefix="" />
    </Grid>
  </Grid>
)

const hasReason = (score: Score) => Boolean((score as Score).reason)
const byScoreDesc = descend(propOr(0, 'score'))

interface EditAppointmentProps {
  appointment?: Appointment
  profileId: string
  scoresWithReason: (Score & { field: string })[]
}

interface EditAppointmentConnectedProps {
  createAppointmentForProfile: ({ profileId, type, details }: { profileId: string; type: string; details: string }) => void
  attendanceEnabled: boolean
}

const EditAppointment = connect<EditAppointmentProps, EditAppointmentConnectedProps>(
  {
    createAppointmentForProfile: sequences`appointments.createAppointmentForProfile`,
    attendanceEnabled: state`account.packages.attendance`,
  },

  ({
    appointment,
    profileId,
    scoresWithReason,
    createAppointmentForProfile,
    attendanceEnabled = false,
  }: EditAppointmentProps & EditAppointmentConnectedProps) => {
    const tooltipTitle = appointment ? 'View Appointment' : 'Create appointment'
    const Icon = appointment ? Schedule : PersonAddAlt1
    const href = appointment ? (routePaths as RoutePaths).toAppointment({ id: appointment._id, returnTo: 'connectionBoard' }) : undefined
    const label = appointment ? (appointment.createdBy?.name?.first || 'Staff') + ' Connecting' : 'Connect'

    return (
      <ButtonGroup color="primary" size="small" variant="outlined" aria-label="Resident connection actions">
        <Tooltip title={tooltipTitle}>
          <Button
            startIcon={<Icon />}
            href={href}
            onClick={() => {
              if (!appointment) {
                createAppointmentForProfile({ profileId, type: 'Life Enrichment', details: pluck('reason', scoresWithReason).join(',') })
              }
            }}
          >
            {label}
          </Button>
        </Tooltip>

        {attendanceEnabled ? (
          <Tooltip title="Log 1:1 activity">
            <Button href={(routePaths as RoutePaths).toOneToOneActivity({ id: 'create', profile: profileId, returnTo: 'connectionBoard' })}>
              <OneToOneActivity height="20" width="20" />
            </Button>
          </Tooltip>
        ) : null}
      </ButtonGroup>
    )
  }
)

const Row = connect<RowProps, RowConnectedProps>(
  {
    item: entityFromState('profiles', props`id`),
    appointmentsByProfile: state`appointments.appointmentsByProfile`,
    appointmentsEnabled: state`account.packages.appointments`,
  },

  ({ item, appointmentsByProfile = {}, appointmentsEnabled = false }: RowProps & RowConnectedProps) => {
    const { _id: id, name, levelOfCare, connectionScore } = item
    const displayName = format.name(name) || '(no name)'
    const [expanded, setExpanded] = useState('')
    const scoresWithReason = Object.entries(connectionScore ?? {})
      .map(([field, score]) => ({ ...(score as Score), field }))
      .filter(hasReason)
      .toSorted(byScoreDesc)
    const appointment = appointmentsByProfile?.[id]

    return (
      <>
        <TableRow>
          <TableCell component="th" scope="row" sx={{ py: 1 }}>
            <Box display="flex" flexDirection="row" alignItems="center">
              <Link href={(routePaths as RoutePaths).toPerson({ id })}>{displayName}</Link>
            </Box>
          </TableCell>

          <TableCell sx={{ py: 1 }}>{levelOfCare || '-'}</TableCell>

          <TableCell sx={{ py: 1 }}>
            <ConnectionScore current={connectionScore?.total} />
          </TableCell>

          <TableCell sx={{ py: 1 }}>
            <Mood score={connectionScore?.mood} />
          </TableCell>

          {appointmentsEnabled ? (
            <TableCell>
              <EditAppointment appointment={appointment} profileId={id} scoresWithReason={scoresWithReason} />
            </TableCell>
          ) : null}

          <TableCell sx={{ py: 1 }}>
            {scoresWithReason.length > 0 ? (
              <IconButton size="small" onClick={() => setExpanded(expanded === id ? '' : id)}>
                {expanded === id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
              </IconButton>
            ) : null}
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0, borderBottom: 0 }} colSpan={6}>
            <Collapse in={expanded === id} timeout="auto" unmountOnExit>
              <ConnectionSignals scoresWithReason={scoresWithReason} />
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    )
  }
)

interface ConnectionBoardProps {
  connectionBoardProfiles: string[]
  findAppointments: (args: { query: { filter: string; profile: object; type: string } }) => void
  appointmentsEnabled: boolean
  filter?: string
  route: { params?: Record<string, string | null>; query?: Record<string, string | null> }
  organization?: Organization
  peopleEnabled?: boolean
}

type ParamTypes = {
  createdAt?: string
  levelOfCare?: string | null
}

const getEmptyContent = (params: ParamTypes) => {
  const { createdAt, levelOfCare } = params

  const hasBeenCreatedRecently = createdAt && DateTime.fromISO(createdAt) > DateTime.now().minus({ days: 30 })

  if (hasBeenCreatedRecently) {
    return "We're still learning your community's rhythm. Keep tracking activities. Meaningful signals usually take about a week to build."
  }

  if (levelOfCare) {
    return "Hmm, nothing fits these filters. There may be residents who'd welcome a connection under different settings."
  }

  return 'All caught up. As signals change through the day, anyone who needs connection will surface here.'
}

const ConnectionBoard = connect(
  {
    connectionBoardProfiles: state`profiles.connectionBoardList.ids`,
    findAppointments: sequences`appointments.find`,
    appointmentsEnabled: state`account.packages.appointments`,
    peopleEnabled: state`account.packages.people`,
    organization: state`account.organization`,
    route: state`route`,
  },

  ({ findAppointments, connectionBoardProfiles, appointmentsEnabled, organization, route = {}, peopleEnabled = false }: ConnectionBoardProps) => {
    const shouldFetchAppointments = Boolean(appointmentsEnabled && connectionBoardProfiles.length > 0)
    const filter = route.query?.filter || 'connectionBoard'
    const levelOfCare = route.params?.levelOfCare || route.query?.levelOfCare

    useEffect(() => {
      if (shouldFetchAppointments) {
        findAppointments({ query: { filter: 'upcoming', profile: { $in: connectionBoardProfiles }, type: 'Life Enrichment' } })
      }
    }, [shouldFetchAppointments])

    const head = (
      <TableRow>
        <TableCell>
          <Sortable name="name.last">Name</Sortable>
        </TableCell>
        <TableCell>
          <Sortable key="levelOfCare" name="levelOfCare">
            Level of care
          </Sortable>
        </TableCell>
        <TableCell>Connection Score</TableCell>
        <TableCell>Today&apos;s Mood</TableCell>
        <TableCell></TableCell>
        {appointmentsEnabled && <TableCell></TableCell>}
      </TableRow>
    )

    const emptyContent = getEmptyContent({ createdAt: organization?.createdAt as string, levelOfCare })

    if (!peopleEnabled) {
      return null
    }

    return (
      <Card sx={{ borderRadius: 2, py: 4, px: 4 }} variant="outlined">
        <TableList
          key={filter}
          // @ts-expect-error TableList does not have types defined
          type="profile"
          title="Connection Board"
          head={head}
          emptyContent={emptyContent}
          emptyScreenProps={{ my: [4, 6] }}
          itemComponent={Row}
          create={false}
          listKey="connectionBoardList"
          findParams={{ query: { filter, $sort: { 'connectionScore.total': -1 } } }}
          intro={intro}
          refreshMs={5 * 60 * 1000} // Five minutes.
        />
      </Card>
    )
  }
)

export default ConnectionBoard
